import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '@hooks/useAuth';
import MetricCard from '@components/analytics/MetricCard';
import {
  getDashboardAnalytics,
  getTopVolunteers,
  getTopEvents,
  getVolunteerGrowthTrend,
  getEventTrend,
} from '@services/analyticsService';

const AnalyticsPage = () => {
  const { userData: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Analytics data
  const [metrics, setMetrics] = useState(null);
  const [topVolunteers, setTopVolunteers] = useState([]);
  const [topEvents, setTopEvents] = useState([]);
  const [volunteerTrend, setVolunteerTrend] = useState([]);
  const [eventTrend, setEventTrend] = useState([]);

  const fetchAnalytics = async () => {
    if (!user?.organizationId) {
      setError('No organization associated with your account.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch all analytics data in parallel
      const [dashboardData, volunteers, events, volTrend, evtTrend] = await Promise.all([
        getDashboardAnalytics(user.organizationId),
        getTopVolunteers(user.organizationId, 10),
        getTopEvents(user.organizationId, 5),
        getVolunteerGrowthTrend(user.organizationId, 6),
        getEventTrend(user.organizationId, 6),
      ]);

      setMetrics(dashboardData);
      setTopVolunteers(volunteers);
      setTopEvents(events);
      setVolunteerTrend(volTrend);
      setEventTrend(evtTrend);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [user?.organizationId]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {user?.organizationName || 'Organization'} Performance Metrics
          </Typography>
        </Box>

        <IconButton
          onClick={fetchAnalytics}
          disabled={loading}
          sx={{
            bgcolor: 'primary.lighter',
            '&:hover': { bgcolor: 'primary.light' },
            minWidth: 48,
            minHeight: 48,
          }}
          aria-label="Refresh analytics"
        >
          {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Metrics Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Events"
            value={metrics?.events?.totalEvents || 0}
            icon={<EventIcon />}
            subtitle={`${metrics?.events?.upcomingEvents || 0} upcoming`}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Volunteers"
            value={metrics?.volunteers?.activeVolunteers || 0}
            icon={<PeopleIcon />}
            subtitle={`${metrics?.volunteers?.pendingVolunteers || 0} pending approval`}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Fulfillment Rate"
            value={`${metrics?.events?.averageFulfillmentRate || 0}%`}
            icon={<TrendingUpIcon />}
            subtitle={`${metrics?.events?.totalVolunteersRegistered || 0} of ${
              metrics?.events?.totalVolunteerSpots || 0
            } spots filled`}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Hours Served"
            value={metrics?.volunteers?.totalHoursServed || 0}
            icon={<AccessTimeIcon />}
            subtitle={`${metrics?.volunteers?.averageHoursPerVolunteer || 0} avg per volunteer`}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Volunteer Growth Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Volunteer Growth (Last 6 Months)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={volunteerTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{ fontSize: '0.75rem' }} />
                <YAxis style={{ fontSize: '0.75rem' }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '0.875rem' }} />
                <Line
                  type="monotone"
                  dataKey="newVolunteers"
                  stroke="#8884d8"
                  name="New Volunteers"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="activeVolunteers"
                  stroke="#82ca9d"
                  name="Total Active"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Event Trend Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Event Activity (Last 6 Months)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{ fontSize: '0.75rem' }} />
                <YAxis style={{ fontSize: '0.75rem' }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '0.875rem' }} />
                <Bar dataKey="totalEvents" fill="#8884d8" name="Total Events" />
                <Bar dataKey="completedEvents" fill="#82ca9d" name="Completed" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Tables */}
      <Grid container spacing={3}>
        {/* Top Volunteers Table */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrophyIcon color="warning" />
              <Typography variant="h6" fontWeight="bold">
                Top Volunteers
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: { xs: 400, md: 500 } }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topVolunteers.length > 0 ? (
                    topVolunteers.map((volunteer, index) => (
                      <TableRow key={volunteer.email} hover>
                        <TableCell>
                          <Chip
                            label={index + 1}
                            size="small"
                            color={index < 3 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {volunteer.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {volunteer.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {volunteer.totalHours} hrs
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No volunteer hours logged yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Events Table */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EventIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Most Popular Events
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: { xs: 400, md: 500 } }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell align="right">Volunteers</TableCell>
                    <TableCell align="right">Fill Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topEvents.length > 0 ? (
                    topEvents.map((event) => (
                      <TableRow key={event.title} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {event.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {event.volunteersRegistered} / {event.volunteersNeeded}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${event.fulfillmentRate}%`}
                            size="small"
                            color={
                              event.fulfillmentRate >= 100
                                ? 'success'
                                : event.fulfillmentRate >= 75
                                ? 'info'
                                : event.fulfillmentRate >= 50
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No events with registrations yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AnalyticsPage;
